"use client";

import * as React from "react";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, LogIn } from "lucide-react";
import config from "@/config";

interface SaveReportSignInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SaveReportSignInDialog({
  open,
  onOpenChange,
}: SaveReportSignInDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm p-6">
        <DialogHeader className="text-center space-y-2">
          <DialogTitle className="text-lg font-semibold">
            Unlock leaderboard rankings
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Sign in once to keep every audit, track your progress, and qualify for the global leaderboard.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2 text-left text-sm text-muted-foreground">
          {[
            "Save every report to your personal history",
            "Compare improvements over time",
            "Appear on the leaderboard when you post top scores",
          ].map((benefit) => (
            <div key={benefit} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-primary" aria-hidden="true" />
              <p>{benefit}.</p>
            </div>
          ))}
        </div>

        <DialogFooter className="mt-6 flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            className="h-10 sm:h-11 w-full sm:flex-1"
            data-slot="cancel"
          >
            Not now
          </Button>
          <Button asChild className="h-10 sm:h-11 gap-2 w-full sm:flex-1" data-slot="signin">
            <Link href={config.routes.signin.path} aria-label="Sign in to save report and appear on leaderboard">
              <LogIn className="h-4 w-4" />
              Sign in for free
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
